import { create } from 'zustand';
import apiClient from '../lib/api';

/**
 * Asset master data: vendors (assets.suppliers) and categories with their
 * custom fields (assets.asset_categories / asset_custom_fields).
 * Actions throw an Error with the server's message on failure.
 */

export interface Vendor {
  id: number;
  name: string;
  email: string | null;
  contactPerson: string | null;
  contactEmail: string | null;
  phone: string | null;
  slaTerms: string | null;
  address: string | null;
  website: string | null;
  notes: string | null;
  assetCount: number;
  poCount: number;
  ticketCount?: number;
  openTicketCount?: number;
  createdAt: string;
}

export interface CategoryField {
  id: number;
  key: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'date' | 'boolean';
  required: boolean;
  options: string[];
}

export interface AssetCategory {
  id: number;
  name: string;
  key: string;
  description: string | null;
  icon: string | null;
  typeId: number | null;
  typeName: string | null;
  assetCount: number;
  fields: CategoryField[];
}

export interface AssetType {
  id: number;
  name: string;
  code: string;
  description: string | null;
  icon: string | null;
  isActive: boolean;
  assetCount: number;
  createdAt: string;
}

export interface VendorInput {
  name: string;
  email?: string;
  contactPerson?: string;
  contactEmail?: string;
  phone?: string;
  slaTerms?: string;
  address?: string;
  website?: string;
  notes?: string;
}

const mapVendor = (v: any): Vendor => ({
  id: v.id,
  name: v.company_name ?? v.name ?? 'Unnamed Vendor',
  email: v.company_mail ?? v.email ?? null,
  contactPerson: v.contact_person ?? v.contactPerson ?? null,
  contactEmail: v.contact_email ?? v.contactEmail ?? null,
  phone: v.contact_number === null || v.contact_number === undefined ? (v.phone ? String(v.phone) : null) : String(v.contact_number),
  slaTerms: v.sla_terms ?? v.slaTerms ?? null,
  address: v.address ?? null,
  website: v.website ?? null,
  notes: v.notes ?? null,
  assetCount: Number(v.asset_count ?? v.assetCount) || 0,
  poCount: Number(v.po_count ?? v.poCount) || 0,
  ticketCount: Number(v.ticket_count ?? v.ticketCount) || 0,
  openTicketCount: Number(v.open_ticket_count ?? v.openTicketCount) || 0,
  createdAt: v.created_at ?? v.createdAt,
});

const mapField = (f: any): CategoryField => ({
  id: f.id,
  key: f.field_key,
  label: f.field_label,
  type: f.field_type,
  required: Boolean(f.required),
  options: Array.isArray(f.options) ? f.options : [],
});

const mapCategory = (c: any): AssetCategory => ({
  id: c.id,
  name: c.name,
  key: c.key,
  description: c.description,
  icon: c.icon,
  typeId: c.type_id ?? null,
  typeName: c.type_name ?? null,
  assetCount: Number(c.asset_count) || 0,
  fields: (c.fields || []).map(mapField),
});

const mapType = (t: any): AssetType => ({
  id: t.id,
  name: t.name,
  code: t.code,
  description: t.description,
  icon: t.icon,
  isActive: Boolean(t.is_active),
  assetCount: Number(t.asset_count) || 0,
  createdAt: t.created_at,
});

const vendorPayload = (v: Partial<VendorInput>) => ({
  company_name: v.name,
  company_mail: v.email,
  contact_person: v.contactPerson,
  contact_email: v.contactEmail,
  contact_number: v.phone,
  sla_terms: v.slaTerms,
  address: v.address,
  website: v.website,
  notes: v.notes,
});

const dataOf = (res: any) => res.data?.data ?? res.data;
const errorOf = (e: any, fallback: string) => new Error(e?.response?.data?.message || e?.message || fallback);

interface CatalogState {
  vendors: Vendor[];
  categories: AssetCategory[];
  types: AssetType[];
  vendorsLoaded: boolean;
  categoriesLoaded: boolean;
  typesLoaded: boolean;
  error: string | null;

  fetchVendors: () => Promise<void>;
  createVendor: (v: VendorInput) => Promise<Vendor>;
  updateVendor: (id: number, v: Partial<VendorInput>) => Promise<Vendor>;
  deleteVendor: (id: number) => Promise<void>;

  fetchCategories: () => Promise<void>;
  createCategory: (c: { name: string; description?: string; icon?: string; type_id?: number | null }) => Promise<AssetCategory>;
  updateCategory: (id: number, c: { name?: string; description?: string; icon?: string; type_id?: number | null }) => Promise<AssetCategory>;
  deleteCategory: (id: number) => Promise<void>;
  addCategoryField: (categoryId: number, f: { label: string; type: CategoryField['type']; required?: boolean; options?: string[] }) => Promise<AssetCategory>;
  deleteCategoryField: (categoryId: number, fieldId: number) => Promise<AssetCategory>;

  fetchTypes: () => Promise<void>;
  createType: (t: { name: string; description?: string; icon?: string }) => Promise<AssetType>;
  updateType: (id: number, t: { name?: string; description?: string; icon?: string; is_active?: boolean }) => Promise<AssetType>;
  deleteType: (id: number) => Promise<void>;
}

export const useAssetCatalogStore = create<CatalogState>((set) => {
  const replaceCategory = (cat: AssetCategory) =>
    set((s) => ({
      categories: s.categories.some((c) => c.id === cat.id)
        ? s.categories.map((c) => (c.id === cat.id ? cat : c))
        : [...s.categories, cat].sort((a, b) => a.name.localeCompare(b.name)),
    }));

  return {
    vendors: [],
    categories: [],
    types: [],
    vendorsLoaded: false,
    categoriesLoaded: false,
    typesLoaded: false,
    error: null,

    fetchVendors: async () => {
      try {
        const rows = dataOf(await apiClient.get('/assets/vendors'));
        set({ vendors: (rows || []).map(mapVendor), vendorsLoaded: true });
      } catch (e) {
        set({ vendorsLoaded: true, error: errorOf(e, 'Could not load vendors').message });
      }
    },

    createVendor: async (v) => {
      try {
        const vendor = mapVendor(dataOf(await apiClient.post('/assets/vendors', vendorPayload(v))));
        set((s) => ({ vendors: [...s.vendors, vendor].sort((a, b) => a.name.localeCompare(b.name)) }));
        return vendor;
      } catch (e) {
        throw errorOf(e, 'Could not add the vendor');
      }
    },

    updateVendor: async (id, v) => {
      try {
        const vendor = mapVendor(dataOf(await apiClient.patch(`/assets/vendors/${id}`, vendorPayload(v))));
        set((s) => ({ vendors: s.vendors.map((x) => (x.id === id ? vendor : x)) }));
        return vendor;
      } catch (e) {
        throw errorOf(e, 'Could not update the vendor');
      }
    },

    deleteVendor: async (id) => {
      try {
        await apiClient.delete(`/assets/vendors/${id}`);
        set((s) => ({ vendors: s.vendors.filter((x) => x.id !== id) }));
      } catch (e) {
        throw errorOf(e, 'Could not delete the vendor');
      }
    },

    fetchCategories: async () => {
      try {
        const rows = dataOf(await apiClient.get('/assets/categories'));
        set({ categories: (rows || []).map(mapCategory), categoriesLoaded: true });
      } catch (e) {
        set({ categoriesLoaded: true, error: errorOf(e, 'Could not load categories').message });
      }
    },

    createCategory: async (c) => {
      try {
        const cat = mapCategory(dataOf(await apiClient.post('/assets/categories', c)));
        replaceCategory(cat);
        return cat;
      } catch (e) {
        throw errorOf(e, 'Could not add the category');
      }
    },

    updateCategory: async (id, c) => {
      try {
        const cat = mapCategory(dataOf(await apiClient.patch(`/assets/categories/${id}`, c)));
        replaceCategory(cat);
        return cat;
      } catch (e) {
        throw errorOf(e, 'Could not update the category');
      }
    },

    deleteCategory: async (id) => {
      try {
        await apiClient.delete(`/assets/categories/${id}`);
        set((s) => ({ categories: s.categories.filter((c) => c.id !== id) }));
      } catch (e) {
        throw errorOf(e, 'Could not delete the category');
      }
    },

    addCategoryField: async (categoryId, f) => {
      try {
        const cat = mapCategory(dataOf(await apiClient.post(`/assets/categories/${categoryId}/fields`, {
          field_label: f.label, field_type: f.type, required: Boolean(f.required), options: f.options,
        })));
        replaceCategory(cat);
        return cat;
      } catch (e) {
        throw errorOf(e, 'Could not add the field');
      }
    },

    deleteCategoryField: async (categoryId, fieldId) => {
      try {
        const cat = mapCategory(dataOf(await apiClient.delete(`/assets/categories/${categoryId}/fields/${fieldId}`)));
        replaceCategory(cat);
        return cat;
      } catch (e) {
        throw errorOf(e, 'Could not remove the field');
      }
    },

    fetchTypes: async () => {
      try {
        const rows = dataOf(await apiClient.get('/assets/types'));
        set({ types: (rows || []).map(mapType), typesLoaded: true });
      } catch (e) {
        set({ typesLoaded: true, error: errorOf(e, 'Could not load asset types').message });
      }
    },

    createType: async (t) => {
      try {
        const item = mapType(dataOf(await apiClient.post('/assets/types', t)));
        set((s) => ({ types: [...s.types, item].sort((a, b) => a.name.localeCompare(b.name)) }));
        return item;
      } catch (e) {
        throw errorOf(e, 'Could not add the asset type');
      }
    },

    updateType: async (id, t) => {
      try {
        const item = mapType(dataOf(await apiClient.patch(`/assets/types/${id}`, t)));
        set((s) => ({ types: s.types.map((x) => (x.id === id ? item : x)) }));
        return item;
      } catch (e) {
        throw errorOf(e, 'Could not update the asset type');
      }
    },

    deleteType: async (id) => {
      try {
        await apiClient.delete(`/assets/types/${id}`);
        set((s) => ({ types: s.types.filter((x) => x.id !== id) }));
      } catch (e) {
        throw errorOf(e, 'Could not delete the asset type');
      }
    },
  };
});
